// Mova: pre-warms Shopify's section-render cache for variant URLs on the
// product detail page (PDP) when the user hovers (desktop), focuses, or
// first touches (mobile) a variant swatch.
//
// The first request for a variant URL is uncached at Shopify's CDN edge and
// can take 300–800ms. By kicking off a low-priority fetch the moment the
// user's intent becomes clear (hover / touchstart / focus), the response is
// usually already in cache by the time `variant-picker.js` issues its real
// fetch on click — making the first variant change feel instant for every
// element that updates on variant change (gallery, price, inventory, ATC).
//
// Scoped to the main PDP variant picker only (data-template-product-match).
// Quick-add modals and product-card pickers are intentionally excluded so
// we don't waste bandwidth prefetching for swatches the user is unlikely to
// commit to.

(() => {
  if (window.__movaVariantPrefetchBooted) return;
  window.__movaVariantPrefetchBooted = true;

  /** @type {Set<string>} */
  const prefetched = new Set();

  /**
   * Builds the same URL `variant-picker.js#buildRequestUrl` would build for
   * a given hovered radio input, but without any side effects on the
   * picker's internal state.
   * @param {HTMLInputElement} input
   * @param {Element} picker
   * @returns {string | null}
   */
  function buildPrefetchUrl(input, picker) {
    const productUrl = input.dataset.connectedProductUrl || picker.getAttribute('data-product-url');
    if (!productUrl) return null;

    // Mirror the picker's `selectedOptionsValues` getter, but swap in the
    // hovered input's option_value_id for its own fieldset.
    const checkedInputs = Array.from(picker.querySelectorAll('fieldset input:checked'));
    const hoveredFieldset = input.closest('fieldset');

    /** @type {string[]} */
    const optionValueIds = [];
    let replacedInHoveredFieldset = false;

    for (const checked of checkedInputs) {
      const checkedFieldset = checked.closest('fieldset');
      if (hoveredFieldset && checkedFieldset === hoveredFieldset) {
        const id = input.dataset.optionValueId;
        if (id) optionValueIds.push(id);
        replacedInHoveredFieldset = true;
      } else {
        const id = checked.dataset.optionValueId;
        if (id) optionValueIds.push(id);
      }
    }

    if (!replacedInHoveredFieldset && input.dataset.optionValueId) {
      optionValueIds.push(input.dataset.optionValueId);
    }

    if (optionValueIds.length === 0) return null;

    const params = new URLSearchParams();
    const view = new URL(window.location.href).searchParams.get('view');
    if (view) params.set('view', view);
    params.set('option_values', optionValueIds.join(','));

    const baseUrl = productUrl.split('?')[0];
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Fires a low-priority GET so the response is cached for the upcoming
   * real fetch. The response body is intentionally discarded.
   * @param {string | null} url
   */
  function prefetchUrl(url) {
    if (!url || prefetched.has(url)) return;
    prefetched.add(url);

    /** @type {RequestInit} */
    const init = {
      method: 'GET',
      credentials: 'same-origin',
      // Soft hint: don't compete with critical resources on the page.
      priority: 'low',
    };

    try {
      fetch(url, init).catch(() => {
        // Allow a retry on next hover if the network blipped.
        prefetched.delete(url);
      });
    } catch {
      prefetched.delete(url);
    }
  }

  /**
   * @param {EventTarget | null} target
   * @returns {{ input: HTMLInputElement; picker: Element } | null}
   */
  function resolveContext(target) {
    if (!(target instanceof Element)) return null;

    const label = target.closest('label.variant-option__button-label');
    if (!label) return null;

    const picker = label.closest('variant-picker[data-template-product-match="true"]');
    if (!picker) return null;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return null;
    if (input.checked) return null;

    return { input, picker };
  }

  /**
   * @param {Event} event
   */
  function handleIntent(event) {
    const ctx = resolveContext(event.target);
    if (!ctx) return;

    const url = buildPrefetchUrl(ctx.input, ctx.picker);
    prefetchUrl(url);
  }

  // mouseover bubbles and refires per child, but the URL Set dedupes —
  // cheap to leave attached at the document level.
  document.addEventListener('mouseover', handleIntent, { passive: true });
  // touchstart fires ~100–300ms before click on tap, which is enough time
  // for a SLA-friendly Shopify response to land before the user lifts.
  document.addEventListener('touchstart', handleIntent, { passive: true, capture: true });
  // Keyboard focus also signals intent — covers accessibility flows.
  document.addEventListener('focusin', handleIntent, { passive: true });
})();
